// News salvate, connected: one infinite query on GET /me/saved for the
// whole months of ?months= and the single weeks of ?weeks= (both absent:
// every bookmark), grouped by month; the date filter panel takes the facets
// of the same answer (years, months and the weeks of each month) and writes
// ?months= and ?weeks=, and carries «Pulisci tutto» (DELETE /me/saved,
// everything) under the months, with its confirmation in line. The
// components only see the groups, the years and the status rows.
import { useMemo, useState } from "react";
import { Button } from "@heroui/react";
import { useSearchParams } from "react-router";

import { PanelStatus, STATUS_BUTTON_CLASS } from "../../components/PanelStatus";
import { NewsSection } from "../../components/news/NewsSection";
import { DateFilterPanel } from "../../components/sidebar/panels";
import { ExpandedNewsCard } from "./ExpandedNewsCard";
import { facetIndex, nextSavedFilter, savedSelection, toSavedGroups, toSavedYears, toSourceTree } from "./adapt";
import { useClearSaved, useSaved, useSources } from "./api";
import { useBookmarks } from "./bookmarks";
import { ListLoading, LoadMore, MORE_FAILED, REFRESH_FAILED } from "./ListStatus";
import { readNewsParams, withNewsParams, type SavedFilter } from "./params";

const FAILED = "News salvate non disponibili.";
const NONE = "Nessuna news salvata: il segnalibro di una news la porta qui.";
const NONE_IN_PERIOD = "Nessuna news salvata nel periodo selezionato.";
const CONFIRM_CLEAR = "Eliminare tutte le news salvate? Non si può annullare.";
const CLEAR_FAILED = "Pulizia non riuscita.";

/** The filter of News salvate in the query string: whole months and single weeks. */
function savedFilterOf(search: URLSearchParams): SavedFilter {
  const { months, weeks } = readNewsParams(search);
  return { months, weeks };
}

export function SavedNews() {
  const [search] = useSearchParams();
  const filter = useMemo(() => savedFilterOf(search), [search]);
  const query = useSaved(filter);
  const catalogue = useSources();
  const tree = useMemo(() => (catalogue.data ? toSourceTree(catalogue.data) : null), [catalogue.data]);
  const bookmarks = useBookmarks();

  const groups = useMemo(
    () => toSavedGroups(query.data?.pages.flatMap((page) => page.items) ?? [], tree, search),
    [query.data, tree, search],
  );
  // the cards of the last filter stand in while the new ones load: the list is ready, not loading
  const hasCards = query.data !== undefined;
  const facets = query.data?.pages[0]?.facets ?? [];

  let status: React.ReactNode = null;
  if (!hasCards && (query.isPending || query.isFetching)) {
    status = <ListLoading />;
  } else if (!hasCards && query.isError) {
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
        {FAILED}
      </PanelStatus>
    );
  } else if (query.isFetchNextPageError) {
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.fetchNextPage()}>
        {MORE_FAILED}
      </PanelStatus>
    );
  } else if (query.isError && !query.isFetching) {
    // a refetch (a new filter, an invalidation) failed while the cards of the last answer stay on the page
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
        {REFRESH_FAILED}
      </PanelStatus>
    );
  } else if (groups.length === 0) {
    // an empty stand-in of the last filter says nothing about the new one
    if (query.isPlaceholderData) status = <ListLoading />;
    // the facets count every bookmark whatever the filter: none at all, or none in the ticked months and weeks
    else status = <PanelStatus indent={false}>{facets.length === 0 ? NONE : NONE_IN_PERIOD}</PanelStatus>;
  } else if (query.hasNextPage) {
    status = (
      <LoadMore
        isDisabled={query.isFetchingNextPage || query.isPlaceholderData}
        onPress={() => void query.fetchNextPage()}
      />
    );
  }

  const failure = bookmarks.failure && (
    <PanelStatus role="alert" indent={false} onRetry={bookmarks.failure.onRetry}>
      {bookmarks.failure.message}
    </PanelStatus>
  );

  if (groups.length === 0) {
    return (
      <>
        {failure}
        {status}
      </>
    );
  }
  return (
    <>
      {failure}
      {groups.map((group, i) => (
        <NewsSection
          key={group.month}
          title={group.month}
          dateLabel={group.dateLabel}
          items={group.items}
          onToggleSaved={bookmarks.toggle}
          card={ExpandedNewsCard}
        >
          {i === groups.length - 1 ? status : null}
        </NewsSection>
      ))}
    </>
  );
}

const NO_FACETS = { weeks: [], byMonth: {} };

/**
 * «Filtro per data», connected: the years, the months and the weeks of each
 * month from the facets of the same answer as the list (shared query), the
 * checkboxes from ?months= and ?weeks= (a month ticked in part is "mixed",
 * and opens on its weeks when the panel first shows, so the ticked ones are
 * in sight). The panel's own loading and empty rows; the error has its
 * «Riprova» in the list, so here it is only said. Under the months, «Pulisci
 * tutto»: it asks for a confirmation in line, then DELETE /me/saved takes
 * every bookmark, whatever is ticked; refused, it says so with «Riprova».
 * The button has nothing to do without a bookmark (the facets count them
 * all).
 */
export function SavedDateFilter({ onCollapse }: { onCollapse: () => void }) {
  const [search, setSearch] = useSearchParams();
  const filter = useMemo(() => savedFilterOf(search), [search]);
  const query = useSaved(filter);
  const clear = useClearSaved();
  const [confirming, setConfirming] = useState(false);
  const page = query.data?.pages[0];
  const years = useMemo(() => (page ? toSavedYears(page) : null), [page]);
  const index = useMemo(() => (page ? facetIndex(page) : NO_FACETS), [page]);
  const selection = useMemo(() => savedSelection(filter, index), [filter, index]);
  // SourceList reads this once, when the years first render: the months ticked in part at that moment
  const mixed = Object.keys(selection).filter((id) => selection[id] === "mixed");

  const onSelect = (id: string, checked: boolean) =>
    setSearch(withNewsParams(search, nextSavedFilter(filter, index, id, checked)));

  const status = page ? "ready" : query.isError && !query.isFetching ? "error" : "loading";
  const hasSaved = (page?.facets.length ?? 0) > 0;
  const actions = (
    <ClearSaved
      confirming={confirming}
      canClear={hasSaved && !clear.isPending}
      failed={clear.isError}
      onAsk={() => {
        clear.reset();
        setConfirming(true);
      }}
      onCancel={() => setConfirming(false)}
      onClear={() => {
        setConfirming(false);
        clear.mutate();
      }}
    />
  );
  return (
    <DateFilterPanel
      onCollapse={onCollapse}
      years={years}
      status={status}
      selection={selection}
      defaultExpandedMonths={mixed}
      onSelect={onSelect}
      actions={actions}
    />
  );
}

type ClearSavedProps = {
  confirming: boolean;
  canClear: boolean;
  failed: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onClear: () => void;
};

/**
 * «Pulisci tutto» and its confirmation in line, built from Platea's own
 * elements as the feeds' deletion is: the row of small white buttons of the
 * panel (FeedsPanel.tsx:194-206), the confirmation as a muted note with
 * «Elimina» / «Annulla» (FeedsPanel.tsx:295-303), the failure as a status
 * row with «Riprova» (src/components/PanelStatus.tsx).
 */
function ClearSaved({ confirming, canClear, failed, onAsk, onCancel, onClear }: ClearSavedProps) {
  return (
    <div className="flex flex-col gap-2.5">
      {confirming ? (
        <div role="note" className="flex flex-wrap items-center gap-2.5 py-2 text-sm leading-6 text-muted">
          <span>{CONFIRM_CLEAR}</span>
          <Button size="sm" onPress={onClear} className={STATUS_BUTTON_CLASS}>
            Elimina
          </Button>
          <Button size="sm" onPress={onCancel} className={STATUS_BUTTON_CLASS}>
            Annulla
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2.5">
          <Button size="sm" isDisabled={!canClear} onPress={onAsk} className={STATUS_BUTTON_CLASS}>
            Pulisci tutto
          </Button>
        </div>
      )}
      {failed && (
        <PanelStatus role="alert" indent={false} onRetry={onClear}>
          {CLEAR_FAILED}
        </PanelStatus>
      )}
    </div>
  );
}
